/**
 * Pending camera photos when signal drops.
 * Blobs live in IndexedDB so they survive a reload. Jest / missing IDB
 * fall back to an in-memory Map for the same API.
 */

const DB_NAME = 'lgl-m-photo-queue-v1';
const STORE = 'photos';

const memoryPhotos = new Map();

export const photoQueueRecord = ({
  projectId,
  disciplineId,
  rowId,
  file,
  tag,
  location = '',
  queuedAt = Date.now(),
  nonce = Math.random().toString(36).slice(2, 8),
}) => ({
  id: `${projectId}:${disciplineId}:${rowId}:${queuedAt}:${nonce}`,
  projectId,
  disciplineId,
  rowId: String(rowId),
  fileName: file?.name || 'photo.jpg',
  fileType: file?.type || 'image/jpeg',
  blob: file,
  tag: tag || null,
  location,
  queuedAt,
});

const openDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('no-idb'));
    return;
  }
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => {
    if (!req.result.objectStoreNames.contains(STORE)) {
      req.result.createObjectStore(STORE, { keyPath: 'id' });
    }
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const idbAll = async () => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : []);
    req.onerror = () => reject(req.error);
  });
};

const idbPut = async (record) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
  });
};

const idbDelete = async (id) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const enqueuePhoto = async (item) => {
  const record = photoQueueRecord(item);
  try {
    await idbPut(record);
  } catch {
    memoryPhotos.set(record.id, record);
  }
  return record;
};

export const loadPhotoQueue = async () => {
  let idb = [];
  try {
    idb = await idbAll();
  } catch {
    idb = [];
  }
  const mem = Array.from(memoryPhotos.values());
  const seen = new Set(idb.map((item) => item.id));
  return [...idb, ...mem.filter((item) => !seen.has(item.id))];
};

export const photosForRow = async (projectId, disciplineId, rowId) => {
  const all = await loadPhotoQueue();
  return all.filter((item) => (
    String(item.projectId) === String(projectId)
    && String(item.disciplineId) === String(disciplineId)
    && String(item.rowId) === String(rowId)
  ));
};

export const removeQueuedPhoto = async (id) => {
  try {
    await idbDelete(id);
  } catch {
    /* ignore */
  }
  memoryPhotos.delete(id);
};

export const fileFromQueued = (item) => {
  if (item.blob instanceof File) return item.blob;
  return new File([item.blob], item.fileName || 'photo.jpg', {
    type: item.fileType || 'image/jpeg',
  });
};

export const previewUrlForQueued = (item) => {
  if (item.previewUrl) return item.previewUrl;
  if (item.blob) return URL.createObjectURL(item.blob);
  return '';
};

export const resetPhotoQueueForTests = async () => {
  memoryPhotos.clear();
  if (typeof indexedDB === 'undefined') return;
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
};
